import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, Plus, Trash2, Edit2, X, Check } from 'lucide-react';
import { expenseService } from '../../services/api';

const CATEGORY_OPTIONS = [
  'Filing Fee', 'Court Costs', 'Service of Process', 'Copies/Printing',
  'Postage', 'Travel', 'Expert Witness', 'Deposition', 'Mediation',
  'Investigation', 'Records', 'Transcripts', 'Other'
];

const CaseExpenses = ({ caseId }) => {
  const [expenses, setExpenses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    description: '',
    category: '',
    quantity: '1',
    billable: true,
  });

  const fetchExpenses = useCallback(async () => {
    try {
      const response = await expenseService.list({ case_id: caseId });
      setExpenses(response.data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  }, [caseId]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().slice(0, 10),
      amount: '',
      description: '',
      category: '',
      quantity: '1',
      billable: true,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        case_id: caseId,
        date: form.date,
        amount: parseFloat(form.amount),
        description: form.description,
        category: form.category || null,
        quantity: parseFloat(form.quantity) || 1,
        billable: form.billable,
      };
      if (editingId) {
        const { case_id, ...updateData } = data;
        await expenseService.update(editingId, updateData);
      } else {
        await expenseService.create(data);
      }
      resetForm();
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (expense) => {
    setForm({
      date: expense.date,
      amount: expense.amount,
      description: expense.description,
      category: expense.category || '',
      quantity: expense.quantity,
      billable: expense.billable,
    });
    setEditingId(expense.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await expenseService.delete(id);
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const totalAmount = expenses.reduce((sum, e) => sum + parseFloat(e.amount) * parseFloat(e.quantity), 0);
  const billableAmount = expenses.filter(e => e.billable).reduce((sum, e) => sum + parseFloat(e.amount) * parseFloat(e.quantity), 0);

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Expenses
          {expenses.length > 0 && (
            <span className="text-xs font-normal text-gray-500">
              ({expenses.length}) - ${totalAmount.toFixed(2)}
            </span>
          )}
        </h3>
        <button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="text-yellow-600 hover:text-yellow-700 p-1"
          title="Add expense"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="border border-gray-200 rounded-lg p-3 mb-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-yellow-500"
                required
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-yellow-500"
              >
                <option value="">Select...</option>
                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-yellow-500"
              placeholder="What was the expense for?"
              required
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-gray-500">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-yellow-500"
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Qty</label>
              <input
                type="number"
                step="1"
                min="1"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-yellow-500"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-1 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.billable}
                  onChange={(e) => setForm({ ...form, billable: e.target.checked })}
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                />
                Billable
              </label>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-1 px-3 py-1 text-sm text-white bg-yellow-600 rounded hover:bg-yellow-700 disabled:opacity-50"
            >
              <Check size={14} />
              {editingId ? 'Update' : 'Add'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
            >
              <X size={14} />
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Expense List */}
      {expenses.length === 0 && !showForm ? (
        <p className="text-sm text-gray-400 text-center py-3">No expenses recorded</p>
      ) : (
        <div className="space-y-1">
          {expenses.map(expense => (
            <div key={expense.id} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded text-sm group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 truncate">{expense.description}</span>
                  {expense.category && (
                    <span className="text-xs text-gray-400">{expense.category}</span>
                  )}
                  {!expense.billable && (
                    <span className="text-xs text-orange-500">Non-billable</span>
                  )}
                  {expense.billed && (
                    <span className="text-xs text-green-600">Billed</span>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(expense.date + 'T00:00:00').toLocaleDateString()}
                  {parseFloat(expense.quantity) > 1 && ` x ${expense.quantity}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">
                  ${(parseFloat(expense.amount) * parseFloat(expense.quantity)).toFixed(2)}
                </span>
                {!expense.billed && (
                  <div className="hidden group-hover:flex items-center gap-1">
                    <button onClick={() => handleEdit(expense)} className="text-gray-400 hover:text-yellow-600 p-0.5">
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => handleDelete(expense.id)} className="text-gray-400 hover:text-red-500 p-0.5">
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {expenses.length > 0 && billableAmount !== totalAmount && (
        <div className="mt-2 pt-2 border-t text-xs text-gray-500 flex justify-between">
          <span>Billable: ${billableAmount.toFixed(2)}</span>
          <span>Non-billable: ${(totalAmount - billableAmount).toFixed(2)}</span>
        </div>
      )}
    </div>
  );
};

export default CaseExpenses;
